// src/features/workspace/SaveViewDialog.tsx
// Name-and-scope dialog for saving the current workspace state as a view.
//
// Used both for "save as new" and for renaming/re-sharing an existing view;
// the caller decides which by passing the view's current values as `initial`.

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Input } from '@react/components';

import { ToggleSwitch } from '@/components/ToggleSwitch';

export interface SaveViewValues {
  name: string;
  isShared: boolean;
  isDefault: boolean;
}

interface SaveViewDialogProps {
  open: boolean;
  /** Pre-filled values — an existing view's, or a suggested name for a new one. */
  initial: SaveViewValues;
  /** Heading; differs between "save view" and "save a copy". */
  title: string;
  isSaving: boolean;
  /** Shown when the current view type cannot be stored exactly (schedule). */
  approximatedViewType?: boolean;
  /** How much the view narrows, so the user knows what they are naming. */
  filterCount: number;
  onSubmit: (values: SaveViewValues) => void;
  onCancel: () => void;
}

export function SaveViewDialog({
  open,
  initial,
  title,
  isSaving,
  approximatedViewType,
  filterCount,
  onSubmit,
  onCancel,
}: SaveViewDialogProps) {
  const { t } = useI18n();
  const [values, setValues] = useState<SaveViewValues>(initial);
  const nameRef = useRef<HTMLInputElement>(null);

  // Re-seed every time the dialog opens: the same instance serves "save as"
  // and "rename", and a stale name from the previous open would be worse than
  // an empty field.
  useEffect(() => {
    if (!open) return;
    setValues(initial);
    // The name is the only thing the user has to supply; land on it rather
    // than making them reach for the field the dialog opened for.
    nameRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const canSubmit = values.name.trim().length > 0 && !isSaving;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onCancel}
        aria-hidden="true"
      />
      <form
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative mx-4 w-full max-w-md rounded-lg border border-border bg-background p-5 shadow-xl"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) onSubmit({ ...values, name: values.name.trim() });
        }}
      >
        <h3 className="text-base font-semibold text-foreground">{title}</h3>

        <p className="mt-1 text-sm text-muted-foreground">
          {filterCount > 0
            ? t('workspace.savedViews.dialog.summary', { count: filterCount })
            : t('workspace.savedViews.dialog.summaryEmpty')}
        </p>

        <div className="mt-4 space-y-4">
          <Input
            ref={nameRef}
            label={t('common.name')}
            value={values.name}
            placeholder={t('workspace.savedViews.dialog.namePlaceholder')}
            onChange={(event) => setValues((prev) => ({ ...prev, name: event.target.value }))}
          />

          <ToggleSwitch
            label={t('workspace.savedViews.dialog.shared')}
            description={t('workspace.savedViews.dialog.sharedDescription')}
            checked={values.isShared}
            onChange={(isShared) => setValues((prev) => ({ ...prev, isShared }))}
          />

          <ToggleSwitch
            label={t('workspace.savedViews.dialog.default')}
            description={t('workspace.savedViews.dialog.defaultDescription')}
            checked={values.isDefault}
            onChange={(isDefault) => setValues((prev) => ({ ...prev, isDefault }))}
          />

          {approximatedViewType && (
            <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              {t('workspace.savedViews.dialog.scheduleNote')}
            </p>
          )}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" size="sm" isLoading={isSaving} disabled={!canSubmit}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
